import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listMilestoneTemplates,
  createMilestoneTemplate,
  updateMilestoneTemplate,
  deleteMilestoneTemplate,
  type MilestoneTemplateDto,
} from '../../lib/milestoneTemplates';
import { listPositions } from '../../lib/positions';
import { listOrgLevels } from '../../lib/orgLevels';
import { toast } from 'sonner';
import { Milestone, Plus, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/kinerja/milestone-templates')({
  component: MilestoneTemplatesPage,
});

function MilestoneTemplatesPage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <MilestoneTemplatesContent />
    </ProtectedRoute>
  );
}

type PositionOption = { id: string; code: string; title: string };
type OrgLevelOption = { id: string; level: number; name: string };

function MilestoneTemplatesContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [templates, setTemplates] = useState<MilestoneTemplateDto[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [orgLevels, setOrgLevels] = useState<OrgLevelOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<MilestoneTemplateDto | null>(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    name: '',
    positionId: '',
    orgLevelId: '',
    milestone30: '',
    milestone90: '',
    milestone180: '',
  };
  const [form, setForm] = useState(emptyForm);

  function resetForm(t?: MilestoneTemplateDto) {
    if (t) {
      setForm({
        name: t.name,
        positionId: t.positionId ?? '',
        orgLevelId: t.orgLevelId ?? '',
        milestone30: t.milestone30 ?? '',
        milestone90: t.milestone90 ?? '',
        milestone180: t.milestone180 ?? '',
      });
    } else {
      setForm(emptyForm);
    }
  }

  function fetchAll() {
    setLoading(true);
    Promise.all([
      listMilestoneTemplates(companyId),
      listPositions(companyId),
      listOrgLevels(companyId),
    ]).then(([tRes, pRes, oRes]) => {
      setLoading(false);
      if (tRes.success && tRes.data) setTemplates(tRes.data.templates);
      if (pRes.success && pRes.data) setPositions(pRes.data.positions ?? []);
      if (oRes.success && oRes.data) setOrgLevels((oRes.data.orgLevels ?? []).sort((a: OrgLevelOption, b: OrgLevelOption) => a.level - b.level));
    });
  }

  useEffect(() => { fetchAll(); }, [companyId]); // eslint-disable-line

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);

    const payload = {
      companyId,
      name: form.name,
      positionId: form.positionId || undefined,
      orgLevelId: form.orgLevelId || undefined,
      milestone30: form.milestone30 || undefined,
      milestone90: form.milestone90 || undefined,
      milestone180: form.milestone180 || undefined,
    };

    const res = editTarget
      ? await updateMilestoneTemplate(editTarget.id, payload)
      : await createMilestoneTemplate(payload);

    setSaving(false);
    if (res.success) {
      toast.success(editTarget ? 'Template updated.' : 'Milestone template created.');
      setShowCreate(false);
      setEditTarget(null);
      resetForm();
      fetchAll();
    } else {
      toast.error(res.error ?? 'Save failed');
    }
  }

  async function handleDelete(t: MilestoneTemplateDto) {
    const res = await deleteMilestoneTemplate(t.id, companyId);
    if (res.success) {
      toast.success('Template deleted.');
      fetchAll();
    } else {
      toast.error(res.error ?? 'Delete failed');
    }
  }

  function getScopeLabel(t: MilestoneTemplateDto) {
    if (t.positionId) {
      const pos = positions.find((p) => p.id === t.positionId);
      return pos ? `${pos.code} — ${pos.title}` : 'Specific position';
    }
    if (t.orgLevelId) {
      const lvl = orgLevels.find((o) => o.id === t.orgLevelId);
      return lvl ? `Level ${lvl.level}: ${lvl.name}` : 'Specific org level';
    }
    return 'All employees (company default)';
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-brand-50">
            <Milestone className="w-5 h-5 text-brand-500" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Milestone Templates</h1>
            <p className="text-sm text-gray-500">
              Standard 30/90/180-day objectives — AI uses these as scaffolding when generating IDPs
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => { resetForm(); setShowCreate(true); }}>
          <Plus className="w-4 h-4 mr-1.5" />
          New Template
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Milestone className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">No milestone templates yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Create standard milestones per role so IDP milestones are consistent.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {templates.map((t) => (
            <Card key={t.id}>
              <CardContent className="py-4 px-5">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-sm text-gray-900">{t.name}</span>
                      <Badge variant="outline" className="text-xs">{getScopeLabel(t)}</Badge>
                    </div>
                    <div className="space-y-1">
                      {t.milestone30 && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium text-gray-800">30d:</span> {t.milestone30}
                        </p>
                      )}
                      {t.milestone90 && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium text-gray-800">90d:</span> {t.milestone90}
                        </p>
                      )}
                      {t.milestone180 && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium text-gray-800">180d:</span> {t.milestone180}
                        </p>
                      )}
                      {!t.milestone30 && !t.milestone90 && !t.milestone180 && (
                        <p className="text-xs text-gray-400 italic">No milestone text defined yet</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => { setEditTarget(t); resetForm(t); setShowCreate(true); }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                      onClick={() => handleDelete(t)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); setEditTarget(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Template' : 'New Milestone Template'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Template Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Software Engineer Onboarding"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Applies to Position</Label>
                <Select value={form.positionId} onValueChange={(v) => setForm((f) => ({ ...f, positionId: v === 'all' ? '' : v, orgLevelId: '' }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Any position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any position</SelectItem>
                    {positions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.code} — {p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Applies to Org Level</Label>
                <Select
                  value={form.orgLevelId}
                  onValueChange={(v) => setForm((f) => ({ ...f, orgLevelId: v === 'all' ? '' : v, positionId: '' }))}
                  disabled={!!form.positionId}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Any level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any level</SelectItem>
                    {orgLevels.map((o) => (
                      <SelectItem key={o.id} value={o.id}>Level {o.level}: {o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">30-Day Milestone</Label>
              <Textarea
                value={form.milestone30}
                onChange={(e) => setForm((f) => ({ ...f, milestone30: e.target.value }))}
                placeholder="What should the employee achieve in the first 30 days?"
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">90-Day Milestone</Label>
              <Textarea
                value={form.milestone90}
                onChange={(e) => setForm((f) => ({ ...f, milestone90: e.target.value }))}
                placeholder="What should the employee achieve by day 90?"
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">180-Day Milestone</Label>
              <Textarea
                value={form.milestone180}
                onChange={(e) => setForm((f) => ({ ...f, milestone180: e.target.value }))}
                placeholder="What should the employee achieve by day 180?"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditTarget(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : editTarget ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
